import "./index.scss";

import { Box, Stack } from "@mui/material";
import Button from "components/Button";
import CheckableButton from "components/CheckableButton";
import SearchBar from "components/SearchBar";
import Tabs from "components/Tabs";
import _ from "lodash";
import React, { FC, memo, useEffect, useMemo, useState } from "react";

type selectedValueType = string | number;
// type selectedValuesTypes = selectedValueType[];

type TSearchableItem = {
  label: string;
  value?: selectedValueType;
  type: string; //Every tab/OptionGroup has a unique type
};

type OptionGroup = {
  label: string;
  options: TSearchableItem[];
};

interface SearchableGroupedListProps {
  options: OptionGroup[];
  callback?: Function;
  preselectedValues?: any[];
}

const tabEnums = [
  "EVERYTHING",
  "ACTIVITIES",
  "MACHINES_CONNECTED_TO_FITLINXX",
  "MACHINES_NOT_CONNECTED_TO_FITLINXX",
  "CLASSES",
];

const SearchableGroupedList: FC<SearchableGroupedListProps> = (props) => {
  const { options = [], preselectedValues, callback } = props;

  const [searchStr, setSearchStr] = useState("");
  const [loadedOptions, setLoadedOptions] = useState(options);
  const [filterOptions, setFilterOptions] = useState(options);
  const [selectedValues, setSelectedValues] = useState<any[]>(preselectedValues || []);
  const [tabs, setTabs] = useState([]);
  const [tabValue, setTabValue] = useState<number>(0);

  const isInSelectedList = (item, selectedList) => {
    return (
      item && selectedList.findIndex((x) => x.value === item.value && x.type === item.type) > -1
    );
  };

  useEffect(() => {
    if (options?.length) {
      setTabs([
        { label: "Everything" },
        ...options.map((e) => ({
          label: e.label,
        })),
      ]);
      setLoadedOptions(options);
      setFilterOptions(options);
    }
  }, [options]);

  //#region event handlers
  const handleSearch = (str: string, tabIndex = tabValue) => {
    //Filter by tab
    let arrayFilteredByTab = [
      ...loadedOptions.filter((x, i) => {
        if (tabIndex === 0) {
          return x;
        }
        return i === tabIndex - 1;
      }),
    ];
    setTabValue(tabIndex);

    //Filter by search string
    function findByLabel(entry) {
      if (!str) {
        return true;
      }
      if (entry.label.toLowerCase().includes(str.toLowerCase())) {
        return true;
      }
      if (Array.isArray(entry.options)) {
        for (let i = 0; i < entry.options.length; i++) {
          if (findByLabel(entry.options[i])) {
            entry.options = [...entry.options.filter(findByLabel)];
            return true;
          }
        }
      }
      return false;
    }

    let optionsToSearchIn = _.cloneDeep(arrayFilteredByTab);
    setFilterOptions([...optionsToSearchIn.filter(findByLabel)]);
    setSearchStr(str);
  };

  const handlePressItem = (e: React.MouseEvent, entry: any) => {
    e.preventDefault();
    const existIndex = selectedValues.findIndex(
      (x) => x.value === entry.value && x.type === entry.type
    );
    if (existIndex > -1) {
      setSelectedValues([
        ...selectedValues.filter((x: any) => !(x.value === entry.value && x.type === entry.type)),
      ]);
    } else {
      setSelectedValues([...selectedValues, entry]);
    }
  };
  //#endregion event handlers

  const countSelectedItems = useMemo(() => {
    const type = tabEnums[tabValue];
    if (tabValue === 0) return selectedValues?.length || 0;
    return selectedValues?.filter((item) => item.type === type)?.length || 0;
  }, [tabValue, selectedValues]);

  return (
    <Stack height={`100%`} className={`searchable-grouped-list`}>
      <Tabs
        value={tabValue}
        onChange={(t) => handleSearch(searchStr, t)}
        tabs={tabs}
        className="tabs"
      />
      <Box className={`searchable-list-container`} flexGrow={1}>
        <Box className="header">
          <SearchBar className="search-bar" value={searchStr} onSearch={handleSearch} />
        </Box>
        <Box className="list">
          {filterOptions.map((group, groupIndex) => (
            <Box key={`optionGroup_${groupIndex}`}>
              <>
                <Box className="optGroupTitle">{group.label}</Box>
                {group.options.map((item, index) => (
                  <CheckableButton
                    key={index}
                    name={item.label}
                    active={isInSelectedList(item, selectedValues)}
                    onClick={(e) => handlePressItem(e, item)}
                  />
                ))}
              </>
            </Box>
          ))}
        </Box>
      </Box>
      <span className="selectedGoalsSummary">{countSelectedItems} items selected</span>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" marginTop="30px">
        <Stack direction="row" alignItems="center" spacing={3}>
          <Button label="Cancel" className="text-button" onClick={() => callback()} />
          <Button
            label="Confirm"
            type="button"
            className="confirm-button"
            onClick={() => callback(selectedValues)}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default memo(SearchableGroupedList);
